import React, { useState, useEffect } from 'react';
import { X, Save, Building2, UserCheck, Trash2, Plus, AlertCircle, Activity, Hash, Tag, Copy, Calendar, MapPin } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { Session } from '../../types';

interface SessionFormModalProps {
  engagementId: string;
  session?: Session | null;
  onClose: () => void;
  isClone?: boolean;
}

export const SessionFormModal: React.FC<SessionFormModalProps> = ({ engagementId, session, onClose, isClone = false }) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    session_date: '', 
    duration_id: '',
    service_type_id: '',
    delivery_mode_id: '',
    host_org_id: '',
    notes: '',
    headcount: 0,
    status: 'upcoming',
    session_hours: 0
  });

  const [staffing, setStaffing] = useState<{person_id: string, role_id: string, name?: string}[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data: durations } = useQuery({ 
    queryKey: ['lookup', 'session_durations'], 
    queryFn: async () => (await supabase.from('session_durations').select('*').order('label')).data || [] 
  });

  const { data: serviceTypes } = useQuery({ 
    queryKey: ['lookup', 'session_service_types'], 
    queryFn: async () => (await supabase.from('session_service_types').select('*').order('label')).data || [] 
  });

  const { data: deliveryModes } = useQuery({ 
    queryKey: ['lookup', 'session_delivery_modes'], 
    queryFn: async () => (await supabase.from('session_delivery_modes').select('*').order('label')).data || [] 
  });

  const { data: engagement } = useQuery({
    queryKey: ['engagement-mode-context', engagementId],
    queryFn: async () => {
      const { data } = await supabase.from('engagements').select('tracking_mode').eq('id', engagementId).single();
      return data;
    }
  });

  const { data: allOrgs } = useQuery({
    queryKey: ['all-organizations-lookup-sess'],
    queryFn: async () => {
      const { data } = await supabase.from('organizations').select('id, name').order('name');
      return (data || []).map(o => ({ id: o.id, name: o.name }));
    }
  });

  const { data: allPeople } = useQuery({
    queryKey: ['all-people-lookup-sess'],
    queryFn: async () => {
      const { data } = await supabase.from('people').select('id, first_name, last_name').order('last_name');
      return (data || []).map(p => ({ id: p.id, name: `${p.first_name} ${p.last_name}` }));
    }
  });

  const { data: sessionStaffRoles } = useQuery({ 
    queryKey: ['lookup', 'session_staff_roles'], 
    queryFn: async () => (await supabase.from('session_staff_roles').select('*').order('label')).data || [] 
  });

  useEffect(() => {
    if (session) {
      const datePart = session.session_datetime ? session.session_datetime.split('T')[0] : '';
      
      setFormData({
        name: isClone ? `${session.name} (Copy)` : (session.name || ''),
        session_date: datePart,
        duration_id: session.duration_id || '',
        service_type_id: session.service_type_id || '',
        delivery_mode_id: session.delivery_mode_id || '',
        host_org_id: session.host_org_id || '',
        notes: session.notes || '',
        headcount: session.headcount_count || 0,
        status: isClone ? 'upcoming' : (session.status || 'upcoming'),
        session_hours: session.session_hours || 0
      });

      const loadStaff = async () => {
        const { data } = await supabase
          .from('session_staff')
          .select('person_id, role_id, people(first_name, last_name)')
          .eq('session_id', session.id);
        
        if (data) {
          setStaffing(data.map((ss: any) => ({
            person_id: ss.person_id,
            role_id: ss.role_id,
            name: `${ss.people?.first_name} ${ss.people?.last_name}`
          })));
        }
      };
      loadStaff();
    }
  }, [session, isClone]);

  const addStaffMember = (personId: string) => {
    if (!personId) return;
    const person = allPeople?.find(p => p.id === personId);
    if (!person || staffing.some(s => s.person_id === person.id)) return;
    const defaultRole = sessionStaffRoles?.[0]?.id || '';
    setStaffing([...staffing, { person_id: person.id, role_id: defaultRole, name: person.name }]);
  };

  const removeStaffMember = (personId: string) => {
    setStaffing(staffing.filter(s => s.person_id !== personId));
  };

  const updateStaffRole = (personId: string, roleId: string) => {
    setStaffing(staffing.map(s => s.person_id === personId ? { ...s, role_id: roleId } : s));
  };

  const upsertSessionMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      setError(null);
      if (!data.session_date) {
        throw new Error('Session date is mandatory.');
      }
      if (!data.service_type_id) {
        throw new Error('Service Type is mandatory. Please select a Service Type.');
      }
      let targetSessionId = session?.id;
      
      const payload = {
        name: data.name,
        engagement_id: engagementId,
        session_datetime: data.session_date, 
        duration_id: data.duration_id || null,
        service_type_id: data.service_type_id || null,
        delivery_mode_id: data.delivery_mode_id || null,
        host_org_id: data.host_org_id || null,
        notes: data.notes,
        status: data.status,
        headcount_count: data.headcount,
        session_hours: data.session_hours || 0,
        created_by_email: profile?.email
      };

      if (session && !isClone) {
        const { error: sErr } = await supabase.from('sessions').update(payload).eq('id', session.id);
        if (sErr) throw sErr;
      } else {
        const { data: newSess, error: iErr } = await supabase.from('sessions').insert([payload]).select().single();
        if (iErr) throw iErr;
        targetSessionId = newSess.id;
      }

      if (session && !isClone) {
        await supabase.from('session_staff').delete().eq('session_id', targetSessionId!);
      }

      if (staffing.length > 0) {
        const staffingPayload = staffing.map(s => ({
          session_id: targetSessionId!,
          person_id: s.person_id,
          role_id: s.role_id
        }));
        await supabase.from('session_staff').insert(staffingPayload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagement-sessions', engagementId] });
      queryClient.invalidateQueries({ queryKey: ['session', session?.id] });
      onClose();
    },
    onError: (err: any) => setError(err.message)
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-4xl rounded shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-8 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-lg font-black uppercase text-slate-900 tracking-tight flex items-center gap-3">
            {isClone ? <Copy size={20} className="text-blue-600" /> : <Plus size={20} className="text-blue-600" />}
            {isClone ? 'Clone Session' : session ? 'Modify Session' : 'New Operational Window'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={24} /></button>
        </div>
        
        <form className="flex-1 p-8 space-y-8 overflow-y-auto" onSubmit={(e) => { e.preventDefault(); upsertSessionMutation.mutate(formData); }}>
          {error && (
            <div className="p-4 bg-rose-50 border border-rose-100 rounded flex items-start gap-3 text-rose-600">
              <AlertCircle size={18} className="shrink-0 mt-0.5" />
              <p className="text-[11px] font-bold uppercase leading-tight">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            <Input label="Session Name" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Session title..." />

            <div className="space-y-1.5">
              <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                <Building2 size={10} className="text-blue-500" /> Host Organization
              </label>
              <SearchableSelect 
                options={allOrgs || []}
                value={formData.host_org_id}
                onChange={val => setFormData({ ...formData, host_org_id: val })}
                placeholder="Select host..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input label="Session Date" type="date" required value={formData.session_date} onChange={e => setFormData({ ...formData, session_date: e.target.value })} />
              <div className="space-y-1.5">
                <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                  <Activity size={10} className="text-blue-500" /> Session Status
                </label>
                <select className="w-full px-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-sm outline-none focus:ring-4 focus:ring-slate-100" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                  <option value="upcoming">Upcoming</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-slate-50 pt-6">
              <div className="space-y-1.5">
                <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  Duration
                </label>
                <select 
                  className="w-full px-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-sm outline-none focus:ring-4 focus:ring-slate-100" 
                  value={formData.duration_id} 
                  onChange={e => setFormData({ ...formData, duration_id: e.target.value })}
                >
                  <option value="">Select Duration...</option>
                  {durations?.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  Delivery Mode
                </label>
                <select 
                  className="w-full px-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-sm outline-none focus:ring-4 focus:ring-slate-100" 
                  value={formData.delivery_mode_id} 
                  onChange={e => setFormData({ ...formData, delivery_mode_id: e.target.value })}
                >
                  <option value="">Select Delivery Mode...</option>
                  {deliveryModes?.map(dm => <option key={dm.id} value={dm.id}>{dm.label}</option>)}
                </select>
              </div>
            </div>

            {/* Service Type & Hours Selection on its own Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-slate-50 pt-6">
              <div className="space-y-1.5">
                <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  Service Type <span className="text-rose-500 font-extrabold">*</span>
                </label>
                <select 
                  required
                  className="w-full px-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-sm outline-none focus:ring-4 focus:ring-slate-100" 
                  value={formData.service_type_id} 
                  onChange={e => setFormData({ ...formData, service_type_id: e.target.value })}
                >
                  <option value="">Select Service Type...</option>
                  {serviceTypes?.map(st => <option key={st.id} value={st.id}>{st.label}</option>)}
                </select>
              </div>

              <div>
                {(() => {
                  const label = serviceTypes?.find(st => st.id === formData.service_type_id)?.label?.toLowerCase() || '';
                  if (label.includes('coaching')) {
                    return (
                      <div className="space-y-1.5 animate-in slide-in-from-left-2">
                        <label className="block text-[9px] font-bold text-teal-600 uppercase tracking-widest ml-1">
                          Coaching Hours
                        </label>
                        <Input 
                          type="number" 
                          step="0.25"
                          min="0"
                          value={formData.session_hours} 
                          onChange={e => setFormData({ ...formData, session_hours: parseFloat(e.target.value) || 0 })} 
                          className="border-teal-200 bg-teal-50/20 font-bold"
                          placeholder="e.g. 1.5"
                        />
                      </div>
                    );
                  }
                  if (label.includes('instruction')) {
                    return (
                      <div className="space-y-1.5 animate-in slide-in-from-left-2">
                        <label className="block text-[9px] font-bold text-blue-600 uppercase tracking-widest ml-1">
                          Instructional Hours
                        </label>
                        <Input 
                          type="number" 
                          step="0.25"
                          min="0"
                          value={formData.session_hours} 
                          onChange={e => setFormData({ ...formData, session_hours: parseFloat(e.target.value) || 0 })} 
                          className="border-blue-200 bg-blue-50/20 font-bold"
                          placeholder="e.g. 1.5"
                        />
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            {engagement?.tracking_mode === 'headcount' && (
              <div className="animate-in slide-in-from-left-2 pt-2">
                <Input 
                  label="# Headcount" 
                  type="number" 
                  min="0"
                  value={formData.headcount} 
                  onChange={e => setFormData({ ...formData, headcount: parseInt(e.target.value) || 0 })} 
                  className="border-blue-200 bg-blue-50/20"
                />
              </div>
            )}
          </div>

          <div className="space-y-4 pt-6 border-t border-slate-50">
             <div className="flex items-center gap-2 text-emerald-600 mb-2">
               <UserCheck size={14} />
               <h3 className="text-[9px] font-bold uppercase tracking-widest">Assign Operational Staffing</h3>
             </div>
             
             <SearchableSelect 
               options={allPeople || []}
               value=""
               onChange={addStaffMember}
               placeholder="Pick Personnel to assign..."
             />

             <div className="grid grid-cols-1 gap-2 mt-4">
               {staffing.map(s => (
                 <div key={s.person_id} className="p-3 bg-slate-50 rounded border border-slate-100 flex items-center justify-between">
                    <span className="text-[10px] font-bold text-slate-900 uppercase tracking-tight">{s.name}</span>
                    <div className="flex items-center gap-2">
                       <select className="bg-white border border-slate-200 rounded px-2 py-1 text-[9px] font-black uppercase outline-none" value={s.role_id} onChange={e => updateStaffRole(s.person_id, e.target.value)}>
                          {sessionStaffRoles?.map(pr => <option key={pr.id} value={pr.id}>{pr.label}</option>)}
                       </select>
                       <button type="button" onClick={() => removeStaffMember(s.person_id)} className="p-1 text-slate-300 hover:text-rose-500 transition-colors"><Trash2 size={14} /></button>
                    </div>
                 </div>
               ))}
               {staffing.length === 0 && <p className="text-center py-4 text-[9px] font-bold text-slate-300 uppercase tracking-widest italic border border-dashed border-slate-200 rounded">No staffing assignments recorded</p>}
             </div>
          </div>

          <div className="space-y-4 pt-6 border-t border-slate-50">
          </div>

          <div className="space-y-1 pt-6 border-t border-slate-50">
            <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">Administrative Notes</label>
            <textarea rows={3} className="w-full px-4 py-3 bg-white border border-slate-200 rounded font-bold text-slate-900 text-sm outline-none focus:ring-4 focus:ring-slate-100 transition-all resize-none shadow-inner" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} />
          </div>
          
          <div className="flex gap-3 pt-4 pb-4">
             <Button type="button" variant="ghost" className="flex-1 py-4" onClick={onClose}>Discard</Button>
             <Button type="submit" className="flex-[2] py-4 shadow-lg" isLoading={upsertSessionMutation.isPending} icon={isClone ? <Copy size={18} /> : <Save size={18} />}>
               {isClone ? 'Create Clone' : 'Commit Sync'}
             </Button>
          </div>
        </form>
      </div>
    </div>
  );
};